import React, { useEffect, useState, useCallback, Suspense } from 'react';
import {
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
  Dropdown,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  debouncedDataLoadWaitMs,
  ERROR_CODES,
  ORDER_TYPES,
  STATUS,
} from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import {
  getProgressPaymentTemplates,
  deleteProgressPaymentTemplates,
} from 'store/requests/progressPayment';
const StoreProgressPaymentAdd = React.lazy(() => import('../StoreProgressPayment/Add'));
const StoreProgressPaymentEdit = React.lazy(() => import('../StoreProgressPayment/Edit'));
const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const ProgressPaymentTableFilters = React.lazy(
  () => import('./ProgressPaymentTableFilters'),
);
const ClosedDates = React.lazy(() => import('./ClosedDates'));
let lastQuery = false;

const ProgressPaymentTemplates = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isClosedDatesModalOpen, setIsClosedDatesModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowsFromTable, setSelectedRowsFromTable] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openStoreProgressPayment, setOpenStoreProgressPayment] = useState(false);
  const [editStoreProgressPayment, setEditStoreProgressPayment] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [25, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 25,
    },
  });

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRowsFromTable(newSelectedRows);
  };
  const rowSelection = {
    selectedRowKeys,
    selectedRowsFromTable,
    onChange: onSelectChange,
  };

  const columns = [
    {
      dataIndex: 'type',
      title: t('templateType'),
      sorter: true,
      showSorterTooltip: false,
      render: (type) => t(type),
    },
    {
      dataIndex: 'name',
      title: t('slotManagement.templateName'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'dateRange',
      title: t('validityDate'),
      sorter: false,
      showSorterTooltip: false,
      render: (text, record) => {
        const formatLocalDate = (unixTimestamp) => {
          const date = new Date(unixTimestamp);
          date.setHours(date.getHours() + 3);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        };

        const formattedStartDate = record.startDate
          ? formatLocalDate(record.startDate)
          : '--';
        const formattedEndDate = record.endDate
          ? formatLocalDate(record.endDate)
          : '--';

        return `${formattedStartDate} - ${formattedEndDate}`;
      },
    },
    {
      title: t('slotManagement.status'),
      dataIndex: 'active',
      sorter: true,
      showSorterTooltip: false,
      render: (active) =>
        active ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      title: t('slotManagement.isMainTemplate'),
      dataIndex: 'main',
      sorter: true,
      showSorterTooltip: false,
      render: (main) =>
        main ? (
          <Tag icon={<CheckCircleOutlined />} color={'green'}>
            {t('yes')}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color={'lightgrey'}>
            {t('no')}
          </Tag>
        ),
    },
    {
      title: t('targetRegion'),
      dataIndex: 'targetArea',
      sorter: false,
      showSorterTooltip: false,
      render: (targetArea) => {
        let regionStr = '';
        if (targetArea?.orderCityIds?.length > 0) {
          regionStr += 'Sipariş İli';
          if (
            targetArea?.branchIds?.length > 0 ||
            targetArea?.storeIds?.length > 0
          ) {
            regionStr += ' - ';
          }
        }
        if (targetArea?.branchIds?.length > 0) {
          regionStr += 'Bölge';
          if (targetArea?.storeIds?.length > 0) {
            regionStr += ' - ';
          }
        }
        if (targetArea?.storeIds?.length > 0) {
          regionStr += 'Mağaza';
        }

        return regionStr;
      },
    },
    {
      title: t('targetGroup'),
      dataIndex: 'targetGroup',
      sorter: true,
      showSorterTooltip: false,
      render: (targetGroup) => t(targetGroup),
    },
    {
      width: 50,
      render: (dataItem) =>
          <Dropdown
            menu={{
              items: [
                {
                  label: isCrudEnabled ? t('edit') : t('showDetail'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    if (dataItem.type === "COURIER") {
                      setIsEditModalOpen(true);
                    } else {
                      setEditStoreProgressPayment(true);
                    }
                    setSelectedItem(dataItem);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
    },
  ];

  function showDeleteTemplate() {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: ` ${selectedRowsFromTable[0]?.name} ${t('slotManagement.deleteTemplate')}`,
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        deleteProgressPaymentTemplates(
          selectedRowsFromTable[0]?.id,
          'force-delete=false',
        )
          .then((res) => {
            notificationDelete(STATUS.SUCCESS);
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          })
          .catch((error) => {
            if (error) {
              const errorCode = error.code;
              const errorMessage = error.message;

              if (
                errorCode ===
                ERROR_CODES.PROGRESS_PAYMENT_TEMPLATE_MAIN_DELETE_EXCEPTION
              ) {
                confirm({
                  title: t('doYouConfirm'),
                  content: errorMessage,
                  onOk: () => {
                    deleteProgressPaymentTemplates(
                      selectedRowsFromTable[0]?.id,
                      'force-delete=true',
                    ).then((res) => {
                      notificationDelete(STATUS.SUCCESS);
                      lastQuery = null;
                      const queryString = getQueryString();
                      loadData(queryString);
                    });
                  },
                  onCancel: () => {},
                });
              }
            }
          });
      },
      onCancel() {},
    });
  }

  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('slotManagement.templateDeleted'),
    });
  };

  const getQueryString = () => {
    let queryString = '';
    let mainTemplateSort = true;

    Object.entries(filters)
      .filter(([item, value]) => value !== null && value !== undefined)
      .forEach(([item, value], idx) => {
        if (idx == 0) {
          queryString +=
            item === 'name'
              ? `filter=${item}=like="${encodeURI(value)}"`
              : `filter=${item}==${encodeURI(value)}`;
        } else {
          queryString +=
            item === 'name'
              ? ` and ${item}=like="${encodeURI(value)}"`
              : ` and ${item}==${encodeURI(value)}`;
        }
      });

    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
      mainTemplateSort = false;
    }
    if (mainTemplateSort) {
      queryString = `${queryString}&sort=main,desc`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getProgressPaymentTemplates(queryString)
        .then((res) => {
          const { content, page } = res;
          setRows(content ? content : []);
          setSelectedRowsFromTable([]);
          setTimeout(() => {
            setSelectedRowKeys([]);
          }, 1000);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('addProgressPayment'),
      icon: <PlusOutlined />,
      disabled: false,
      dropdown: {
        items: [
          {
            key: '1',
            label: t('addCourierProgressPayment'),
            onClick: () => setIsAddModalOpen(true),
          },
          {
            key: '2',
            label: t('addStoreProgressPayment'),
            onClick: () => setOpenStoreProgressPayment(true),
          },
        ],
      },
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('slotManagement.delete'),
      icon: <DeleteOutlined />,
      onClick: showDeleteTemplate,
      disabled: selectedRowKeys.length > 1 || selectedRowKeys.length < 1,
      dropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('closedDates'),
      icon: <CalendarOutlined />,
      onClick: () => setIsClosedDatesModalOpen(true),
      disabled: false,
      dropdown: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      const button = (
        <Button
          type={item.type}
          shape="round"
          icon={item.icon}
          onClick={item.onClick}
          disabled={item.disabled}
          style={{ marginBottom: '10px', marginRight: '3px' }}
          danger={item.danger}
        />
      );

      return (
        <Tooltip title={item.title} key={idx}>
          {isCrudEnabled && item.dropdown ? (
            <Dropdown
              menu={{ items: item.dropdown.items }}
              arrow
              trigger={['click']}
              placement="bottom"
            >
              {button}
            </Dropdown>
          ) : (
            button
          )}
        </Tooltip>
      );
    });
  };

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item] !== null && filters[item] !== undefined) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            rowSelection={rowSelection}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <ProgressPaymentTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedTemplate={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
            isCrudEnabled={isCrudEnabled}
          />
        )}
        {isClosedDatesModalOpen && (
          <ClosedDates
            onCloseHandler={() => setIsClosedDatesModalOpen(false)}
          />
        )}
        {openStoreProgressPayment && (
          <StoreProgressPaymentAdd
            onCloseHandler={() => setOpenStoreProgressPayment(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {editStoreProgressPayment && (
          <StoreProgressPaymentEdit
            selectedTemplate={selectedItem}
            onCloseHandler={() => setEditStoreProgressPayment(false)}
            onSuccessCallback={fetchData}
            isCrudEnabled={isCrudEnabled}
          />
        )}
      </Suspense>
    </div>
  );
};

export default ProgressPaymentTemplates;
